import axios from "axios";

/**
 * Backend-URL-Auflösung mit Same-Origin-Fallback.
 *
 * - Wenn `REACT_APP_BACKEND_URL` explizit gesetzt ist (nicht leer, nicht "undefined"),
 *   wird der Wert als absolute Basis-URL genutzt — typischerweise für Dev/Preview,
 *   wenn Frontend und Backend auf unterschiedlichen Origins laufen.
 * - Wenn die ENV leer ist oder fehlt, nutzen wir RELATIVE URLs ("") — die Browser
 *   schicken Requests dann an dieselbe Origin, von der das Frontend ausgeliefert
 *   wurde. Same-Origin = keine CORS-Sorgen, Cookies werden automatisch mitgesendet.
 *
 * Production-Empfehlung (Custom-Domain wie newton.heinrichs.group):
 *   `REACT_APP_BACKEND_URL` LEER LASSEN — Emergent proxied /api/* automatisch
 *   unter der Custom-Domain. Same-Origin ist die robusteste Konfiguration.
 */
const rawBackendUrl = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL =
  rawBackendUrl && rawBackendUrl.trim() && rawBackendUrl !== "undefined"
    ? rawBackendUrl.replace(/\/$/, "")
    : ""; // Same-Origin (relative URLs)
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// -----------------------------------------------------------------------------
// 401-Response-Interceptor (Sitzung abgelaufen / Cookie weg / Auth fehlt)
//
// Verhalten:
//   - Bei 401 auf "User-Calls" (alles außer Auth-Routen, Admin-Spezial-Calls,
//     Public-Magic-Links) wird sessionStorage.session_expired = "1" gesetzt
//     UND zu /login redirected.
//   - AuthContext räumt das Flag bei erfolgreichem Login + /auth/me wieder auf.
//
// Ausnahmen (kein Redirect):
//   - /auth/login, /auth/me, /auth/logout, /auth/change-password
//     → AuthContext handlet selbst
//   - /admin/* → kann 401/403 für non-admin User liefern, ist erwartet,
//     Component zeigt eigenes Banner
//   - /datenraum/{token}/* → Public-Magic-Link für externe Investoren
// -----------------------------------------------------------------------------
const NO_REDIRECT_PATHS = ["/auth/login", "/auth/me", "/auth/logout", "/auth/change-password"];

function shouldSkip401Redirect(url) {
  if (!url) return true;
  if (NO_REDIRECT_PATHS.some((p) => url.includes(p))) return true;
  if (url.includes("/admin/")) return true;       // Admin-Calls handlen selbst
  if (url.startsWith("/datenraum/")) return true; // Public-Magic-Link-Routen
  return false;
}

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";

    // 401: harter Redirect zu /login (außer Whitelist)
    if (status === 401 && !shouldSkip401Redirect(url)) {
      // Nur Flag + Redirect setzen, wenn wir NICHT bereits auf /login sind.
      // Vermeidet Redirect-Loops bei iOS-Safari-Cookie-Drops während des Mounts.
      const isLogin =
        typeof window !== "undefined" && window.location?.pathname?.startsWith("/login");
      if (!isLogin && typeof window !== "undefined") {
        try { window.sessionStorage.setItem("session_expired", "1"); } catch { /* ignore */ }
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?session=expired&next=${next}`;
      }
    }

    // Pydantic-Validation-Errors (HTTP 422): detail ist ein Array von Objekten —
    // diese müssen für Toast-Anzeige in lesbaren Text konvertiert werden, sonst
    // crasht React beim Render mit "Objects are not valid as a React child".
    if (status === 422 && Array.isArray(error?.response?.data?.detail)) {
      const msgs = error.response.data.detail.map((err) => {
        const field = Array.isArray(err.loc) ? err.loc.slice(1).join(".") : "?";
        return `${field}: ${err.msg}`;
      });
      // Überschreibe `detail` mit einem String, damit toast.error es rendern kann
      error.response.data.detail = msgs.join("; ");
    }

    return Promise.reject(error);
  }
);

/** Hilfsfunktion: Extrahiert eine lesbare Fehlermeldung aus einer Axios-Exception.
 *  Robust gegen Pydantic-Arrays, fehlende detail-Felder, Netzwerk-Fehler.
 */
export function errorMessage(ex, fallback = "Vorgang fehlgeschlagen") {
  const d = ex?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    return d.map((e) => (typeof e === "string" ? e : `${(e.loc || []).slice(1).join(".")}: ${e.msg}`)).join("; ");
  }
  if (d && typeof d === "object" && d.msg) return d.msg;
  if (!ex?.response) return "Server nicht erreichbar";
  return fallback;
}

// --- Property
export async function fetchProperty() {
  const { data } = await api.get("/property");
  return data;
}
export async function updateProperty(payload) {
  const { data } = await api.put("/property", payload);
  return data;
}

// --- Users
export async function fetchUsers() {
  const { data } = await api.get("/users");
  return data;
}

// --- Investors
export async function listInvestors(params = {}) {
  const { data } = await api.get("/investors", { params });
  return data;
}
export async function getInvestor(id) {
  const { data } = await api.get(`/investors/${id}`);
  return data;
}
export async function createInvestor(payload) {
  const { data } = await api.post("/investors", payload);
  return data;
}
export async function updateInvestor(id, payload) {
  const { data } = await api.put(`/investors/${id}`, payload);
  return data;
}
export async function deleteInvestor(id) {
  const { data } = await api.delete(`/investors/${id}`);
  return data;
}
export async function setSperre(id, richtung, gesperrt, grund) {
  const { data } = await api.post(`/investors/${id}/sperren`, { richtung, gesperrt, grund });
  return data;
}
export async function fetchInvestorStats(betreuer) {
  const { data } = await api.get("/investors/stats", { params: betreuer ? { betreuer } : {} });
  return data;
}
export async function fetchHotLeads(limit = 20) {
  const { data } = await api.get("/investors/hot-leads", { params: { limit } });
  return data;
}
export async function fetchSweetSpot(limit = 5) {
  const { data } = await api.get("/investors/sweet-spot", { params: { limit } });
  return data;
}
export async function listInvestorsGrouped(params = {}) {
  // params: { by, search, only_bnp_exklusiv, exclude_bnp_exklusiv, ebr_affinitaet, ls_affinitaet, tier_max, betreuer }
  const cleaned = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== false) cleaned[k] = v;
  });
  const { data } = await api.get("/investors/grouped", { params: cleaned });
  return data;
}

// --- Phase 6.9b: Exposé-Verteilung
export async function listExposeLinks(params = {}) {
  const cleaned = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== false) cleaned[k] = v;
  });
  const { data } = await api.get("/expose/links", { params: cleaned });
  return data;
}
export async function createExposeLink(payload) {
  const { data } = await api.post("/expose/links", payload);
  return data;
}
export async function patchExposeLink(linkId, payload) {
  const { data } = await api.patch(`/expose/links/${linkId}`, payload);
  return data;
}
export async function deleteExposeLink(linkId, { hard = false } = {}) {
  const { data } = await api.delete(`/expose/links/${linkId}`, { params: hard ? { hard: true } : {} });
  return data;
}
export async function archiveExposeLink(linkId, note = "") {
  const { data } = await api.patch(`/expose/links/${linkId}/archive`, { note });
  return data;
}
export async function unarchiveExposeLink(linkId) {
  const { data } = await api.patch(`/expose/links/${linkId}/unarchive`);
  return data;
}
// Welle 9: PDF-Export
export async function generateExposePdf(linkId) {
  const { data } = await api.post(`/expose/${linkId}/generate-pdf`);
  return data;
}
export async function getExposePdfStatus(linkId) {
  const { data } = await api.get(`/expose/${linkId}/pdf-status`);
  return data;
}
export function exposePdfDownloadUrl(linkId) {
  return `${API}/expose/${linkId}/download-pdf`;
}
export async function getExposeLink(linkId) {
  const { data } = await api.get(`/expose/links/${linkId}`);
  return data;
}
// Public-Endpoints (kein Auth) — nur Meta-Check aus dem Public-Viewer.
// 8-Sekunden-Timeout damit ein hängender/langsamer Endpoint nicht zu endlosem
// "Exposé wird geladen…" führt. Der axios-Timeout wirft ECONNABORTED, das
// von ExposePublic als generischer Fehler behandelt wird.
export async function checkExposeToken(token) {
  const { data } = await api.get(`/expose/${token}`, { timeout: 8000 });
  return data;
}
export function exposeIframeUrl(token) {
  return `${API}/expose/${token}/html`;
}
export function exposePublicUrl(token) {
  // Sichtbare URL für Empfänger (kein /api-Prefix). Nutzt REACT_APP_BACKEND_URL
  // ohne den /api-Suffix — das ist die Preview- bzw. Prod-Domain.
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${base}/expose/${token}`;
}


export async function setDokumentStatus(id, payload) {
  const { data } = await api.put(`/investors/${id}/dokument-status`, payload);
  return data;
}
export async function setDatenraum(id, payload) {
  const { data } = await api.put(`/investors/${id}/datenraum`, payload);
  return data;
}
export async function fetchMailtoDraft(id, typ) {
  const { data } = await api.get(`/investors/${id}/mailto`, { params: { typ } });
  return data;
}

// --- Phase 6.3: Multi-Ansprechpartner CRUD
export async function listAnsprechpartner(invId) {
  const { data } = await api.get(`/investors/${invId}/ansprechpartner`);
  return data;
}
export async function addAnsprechpartner(invId, payload) {
  const { data } = await api.post(`/investors/${invId}/ansprechpartner`, payload);
  return data;
}
export async function updateAnsprechpartner(invId, apId, payload) {
  const { data } = await api.put(`/investors/${invId}/ansprechpartner/${apId}`, payload);
  return data;
}
export async function deleteAnsprechpartner(invId, apId) {
  const { data } = await api.delete(`/investors/${invId}/ansprechpartner/${apId}`);
  return data;
}
export const ROLLE_TAGS = ["CEO/GF", "Investment", "Asset Mgmt", "Legal", "Family Member", "Berater"];

// --- Project Documents
export async function listDocuments() {
  const { data } = await api.get("/documents");
  return data;
}
export async function uploadDocument(file, typ, version, beschreibung) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("typ", typ);
  if (version) fd.append("version", version);
  if (beschreibung) fd.append("beschreibung", beschreibung);
  fd.append("hochgeladen_von", "Heinrichs");
  const { data } = await api.post("/documents/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
export async function deleteDocument(id) {
  const { data } = await api.delete(`/documents/${id}`);
  return data;
}
export function documentDownloadUrl(id) {
  return `${API}/documents/${id}/download`;
}

// --- Phase 3: Bulk + Kontakthistorie + Activity-Log + Reminder + Stats
export async function bulkUpdateInvestors(ids, updates) {
  const { data } = await api.post("/investors/bulk-update", { ids, updates });
  return data;
}
export async function fetchKontakthistorie(id) {
  const { data } = await api.get(`/investors/${id}/kontakthistorie`);
  return data;
}
export async function addKontakthistorie(id, payload) {
  const { data } = await api.post(`/investors/${id}/kontakthistorie`, payload);
  return data;
}
export async function updateKontakthistorie(invId, eintragId, payload) {
  const { data } = await api.put(`/investors/${invId}/kontakthistorie/${eintragId}`, payload);
  return data;
}
export async function deleteKontakthistorie(invId, eintragId) {
  const { data } = await api.delete(`/investors/${invId}/kontakthistorie/${eintragId}`);
  return data;
}
export async function markReminderSent(id) {
  const { data } = await api.post(`/investors/${id}/reminder-versandt`);
  return data;
}
export async function markDatenraumEinladung(id, kommentar = null) {
  const { data } = await api.post(`/investors/${id}/datenraum/einladung`, { kommentar });
  return data;
}
export async function listActivity(params = {}) {
  const { data } = await api.get("/activity-log", { params });
  return data;
}
export async function activitySummary(days = 7) {
  const { data } = await api.get("/activity-log/summary", { params: { days } });
  return data;
}
export async function fetchNdaReminders(thresholdDays = 14) {
  const { data } = await api.get("/reminders/nda", { params: { threshold_days: thresholdDays } });
  return data;
}
export async function statsByBetreuer() {
  const { data } = await api.get("/stats/by-betreuer");
  return data;
}
export async function statsTopHotLeads(limit = 10) {
  const { data } = await api.get("/stats/top-hot-leads", { params: { limit } });
  return data;
}
export async function statsRecentActivity(days = 30) {
  const { data } = await api.get("/stats/recent-activity", { params: { days } });
  return data;
}
export async function previewExcel(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/investors/preview", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
export async function importExcel(file, quelle, columnMapping) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("quelle", quelle);
  if (columnMapping) fd.append("column_mapping", JSON.stringify(columnMapping));
  const { data } = await api.post("/investors/import", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}


// --- Phase 4: Reports
export const reportPdfUrl = () => `${API}/reports/eigentuemer-bericht.pdf`;
export const excelExportUrl = () => `${API}/exports/investoren.xlsx`;


// --- Phase 6: Datenraum (Team)
export async function drListFiles(params = {}) {
  const { data } = await api.get("/dataroom/files", { params });
  return data;
}
export async function drUploadFile(file, { kategorie, beschreibung, version }) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kategorie", kategorie || "Sonstiges");
  fd.append("beschreibung", beschreibung || "");
  fd.append("version", version || "");
  const { data } = await api.post("/dataroom/files", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
export async function drUpdateFile(fileId, payload) {
  const { data } = await api.put(`/dataroom/files/${fileId}`, payload);
  return data;
}
export async function drDeleteFile(fileId) {
  const { data } = await api.delete(`/dataroom/files/${fileId}`);
  return data;
}
export function drTeamDownloadUrl(fileId) {
  return `${API}/dataroom/files/${fileId}/download`;
}
export async function drFileAssignments(fileId) {
  const { data } = await api.get(`/dataroom/files/${fileId}/assignments`);
  return data;
}
export async function drCreateAssignment(fileId, investorId) {
  const { data } = await api.post("/dataroom/assignments", {
    file_id: fileId, investor_id: investorId || null,
  });
  return data;
}
export async function drDeleteAssignment(assignmentId) {
  const { data } = await api.delete(`/dataroom/assignments/${assignmentId}`);
  return data;
}
export async function drInvestorAssignments(investorId) {
  const { data } = await api.get(`/investors/${investorId}/dataroom/assignments`);
  return data;
}
export async function drCreateMagicLink(investorId, ablaufDatum, maxZugriffe = null) {
  const { data } = await api.post(`/investors/${investorId}/dataroom/magic-link`, {
    ablauf_datum: ablaufDatum, max_zugriffe: maxZugriffe,
  });
  return data;
}
export async function drListMagicLinks(investorId) {
  const { data } = await api.get(`/investors/${investorId}/dataroom/magic-links`);
  return data;
}
export async function drDeactivateMagicLink(token) {
  const { data } = await api.post(`/dataroom/magic-links/${token}/deaktivieren`);
  return data;
}
export async function drStats() {
  const { data } = await api.get("/dataroom/stats");
  return data;
}
export async function drAccessLog(params = {}) {
  const { data } = await api.get("/dataroom/access-log", { params });
  return data;
}
export async function drKategorien() {
  const { data } = await api.get("/dataroom/kategorien");
  return data;
}
export async function drAllMagicLinks(onlyAktiv = false) {
  const { data } = await api.get("/dataroom/magic-links", {
    params: { only_aktiv: onlyAktiv ? "true" : undefined },
  });
  return data;
}
export async function drInvestorsSummary() {
  const { data } = await api.get("/dataroom/investors-summary");
  return data;
}

// --- Phase 6: Datenraum (Investor-Public, kein Auth-Cookie nötig — Token in URL)
export async function drInvestorView(token) {
  const { data } = await api.get(`/datenraum/${token}`);
  return data;
}
export function drInvestorDownloadUrl(token, fileId) {
  return `${API}/datenraum/${token}/files/${fileId}/download`;
}

// --- Phase 6.5: Vermietung / Rent Roll
export async function vmListMieteinheiten() {
  const { data } = await api.get("/vermietung/mieteinheiten");
  return data;
}
export async function vmGetKpis(scope = "aktuell") {
  const { data } = await api.get("/vermietung/kpis", { params: { scope } });
  return data;
}
export function vmExportCsvUrl() {
  return `${API}/vermietung/export/csv`;
}

// --- Phase 6.6: ZIP-Jobs (Disk-basiert, ersetzt direkten ZIP-Stream)
export async function zjCreate(payload) {
  const { data } = await api.post("/dataroom/zip-jobs", payload);
  return data;
}
export async function zjGet(jobId) {
  const { data } = await api.get(`/dataroom/zip-jobs/${jobId}`);
  return data;
}
export function zjDownloadUrl(jobId) {
  return `${API}/dataroom/zip-jobs/${jobId}/download`;
}

// --- Phase 6.6: Chunked Uploads (Resume-fähig)
export async function upInit(payload) {
  const { data } = await api.post("/dataroom/uploads/init", payload);
  return data;
}
export async function upPart(uploadId, partN, blob) {
  const { data } = await api.put(
    `/dataroom/uploads/${uploadId}/parts/${partN}`,
    blob,
    { headers: { "Content-Type": "application/octet-stream" } },
  );
  return data;
}
export async function upStatus(uploadId) {
  const { data } = await api.get(`/dataroom/uploads/${uploadId}/status`);
  return data;
}
export async function upComplete(uploadId) {
  const { data } = await api.post(`/dataroom/uploads/${uploadId}/complete`);
  return data;
}
export async function upCancel(uploadId) {
  const { data } = await api.delete(`/dataroom/uploads/${uploadId}`);
  return data;
}

// --- Phase 6.7: Folder-Upload (Bulk-Categorize + Multi-File Chunked Upload)
// Bei sehr vielen Files chunked Ingress-413 vermeiden: Backend in 800er-Batches anrufen
// und Results auf Client-Seite zusammenführen.
export async function upCategorizeBulk(paths) {
  const BATCH = 800;
  if (paths.length <= BATCH) {
    const { data } = await api.post("/dataroom/uploads/categorize-bulk", { paths });
    return data;
  }
  const allItems = [];
  const summary = { matched: 0, unmatched: 0, intern: 0 };
  for (let i = 0; i < paths.length; i += BATCH) {
    const slice = paths.slice(i, i + BATCH);
    const { data } = await api.post("/dataroom/uploads/categorize-bulk", { paths: slice });
    allItems.push(...(data.items || []));
    if (data.summary) {
      summary.matched += data.summary.matched || 0;
      summary.unmatched += data.summary.unmatched || 0;
      summary.intern += data.summary.intern || 0;
    }
  }
  return { items: allItems, summary };
}
export async function upFolderCategories() {
  const { data } = await api.get("/dataroom/uploads/folder-upload/categories");
  return data;
}

// --- Phase 6.8: Akkordion-Aggregat für /dokumente
export async function drCategoriesSummary({ includeIntern = true } = {}) {
  const { data } = await api.get("/dataroom/categories-summary", {
    params: { include_intern: includeIntern },
  });
  return data;
}
export function drTeamThumbUrl(fileId) {
  return `${API}/dataroom/files/${fileId}/thumb`;
}
export function drTeamInlineUrl(fileId) {
  return `${API}/dataroom/files/${fileId}/inline`;
}
export function drInvestorThumbUrl(token, fileId) {
  return `${API}/datenraum/${token}/files/${fileId}/thumb`;
}
export function drInvestorInlineUrl(token, fileId) {
  return `${API}/datenraum/${token}/files/${fileId}/inline`;
}

// --- Welle 5: Speed-Qualifizierung (Quick-Actions + Timeline)
export async function investorQuickAction(id, action) {
  // action: "advance" | "regress" | "disqualify" | "pin_up" | "pin_down" | "unpin"
  const { data } = await api.post(`/investors/${id}/quick-action`, { action });
  return data;
}
export async function investorUndoAction(id) {
  const { data } = await api.post(`/investors/${id}/undo-last-action`);
  return data;
}
export async function fetchInvestorTimeline(id, limit = 100) {
  const { data } = await api.get(`/investors/${id}/timeline`, { params: { limit } });
  return data;
}

// --- Welle 5: Admin-User-Management
export async function adminListUsers() {
  const { data } = await api.get("/admin/users");
  return data;
}
export async function adminCreateUser(payload) {
  const { data } = await api.post("/admin/users", payload);
  return data;
}
export async function adminPatchUser(userId, payload) {
  const { data } = await api.patch(`/admin/users/${userId}`, payload);
  return data;
}
export async function adminResetUserPassword(userId, newPassword) {
  const { data } = await api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
  return data;
}
export async function adminDeleteUser(userId, force = false) {
  const { data } = await api.delete(`/admin/users/${userId}`, { params: { force } });
  return data;
}
export async function adminGeneratePassword() {
  const { data } = await api.post("/admin/users/generate-password");
  return data;
}